import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Factory, X, Save, FileDown, ClipboardList, Ban } from "lucide-react";
import { fdate } from "@/pages/portal/Dashboard";

const COLUMNS = ["Planejamento", "Aguardando início", "Em produção", "Pausada", "Concluída", "Cancelada"];

// OPs antigas gravadas com status do conjunto anterior seguem visíveis no board
const LEGACY_OP_STATUS = { "Controle de qualidade": "Em produção", "Concluído": "Concluída", "Entrega": "Concluída" };
const displayStatus = (s) => LEGACY_OP_STATUS[s] || s;
const PRIORITIES = ["Baixa", "Normal", "Alta", "Urgente"];

const inputCls =
  "mt-1.5 w-full bg-slate-900/70 border border-slate-700 rounded-md px-3 py-2.5 text-sm outline-none focus:border-orange-500 transition-colors placeholder:text-slate-600";
const labelCls = "text-xs uppercase tracking-wider text-slate-400";

const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\s+/g, "-");
const brl = (v) => (v ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const IMG_EXTS = ["png", "jpg", "jpeg", "webp"];

function OpThumb({ oid, itemIdx, files }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      const list = files || [];
      const ti = list.findIndex((f) => f.thumbnail_path);
      const ii = list.findIndex((f) => IMG_EXTS.includes((f.original_filename || "").toLowerCase().split(".").pop()));
      const pick = ti >= 0 ? { fi: ti, thumb: true } : ii >= 0 ? { fi: ii, thumb: false } : null;
      if (pick == null) return;
      try {
        const { data } = await api.get(
          `/production-orders/${oid}/files/${itemIdx}/${pick.fi}${pick.thumb ? "/thumbnail" : ""}`,
          { responseType: "blob" });
        if (alive) setUrl(URL.createObjectURL(data));
      } catch {}
    })();
    return () => { alive = false; };
  }, [oid, itemIdx]);
  return (
    <div className="w-16 h-12 shrink-0 rounded-sm border border-slate-800 bg-slate-950 grid place-content-center overflow-hidden"
      data-testid={`op-thumb-${itemIdx}`}>
      {url ? <img src={url} alt="Prévia da peça" className="w-full h-full object-contain" />
           : <Factory className="w-4 h-4 text-slate-700" />}
    </div>
  );
}

export default function AdminProducao() {
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [pdfLoading, setPdfLoading] = useState("");
  const [machines, setMachines] = useState([]);
  const [receipts, setReceipts] = useState(null);
  const [saldos, setSaldos] = useState([]);
  const [rrNumber, setRrNumber] = useState("");
  const [rrOpen, setRrOpen] = useState(false);
  const [rrForm, setRrForm] = useState({ items: {}, receiver_name: "", delivered_by: "", notes: "" });
  const [rrSaving, setRrSaving] = useState(false);
  const [rrPdfLoading, setRrPdfLoading] = useState("");
  const [finData, setFinData] = useState(null);

  const load = () => api.get("/production-orders").then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/machines").then(({ data }) => setMachines(data || [])).catch(() => {});
  }, []);

  const loadReceipts = async (oid) => {
    try {
      const { data } = await api.get(`/production-orders/${oid}/pickup-receipts`);
      setReceipts(data.receipts || []);
      setSaldos(data.saldos || []);
      setRrNumber(data.number || "");
      const fin = await api.get(`/production-orders/${oid}/financeiro`);
      setFinData(fin.data);
    } catch {
      setReceipts([]);
    }
  };

  const refreshOp = async (oid) => {
    try {
      const { data } = await api.get(`/production-orders/${oid}`);
      setSel(data);
      loadReceipts(oid);
    } catch {}
  };

  const open = (op) => {
    setSel(op);
    setForm({
      status: op.status, machine: op.machine || "", operator: op.operator || "",
      deadline: op.deadline || "", priority: op.priority || "Normal", notes: op.notes || "",
    });
    setRrOpen(false);
    setRrForm({ items: {}, receiver_name: "", delivered_by: "", notes: "" });
    loadReceipts(op.id);
  };

  const createReceipt = async () => {
    const items = Object.entries(rrForm.items)
      .map(([i, q]) => ({ item_index: Number(i), quantity: Number(q) || 0 }))
      .filter((it) => it.quantity > 0);
    if (!items.length) {
      toast.error("Informe a quantidade retirada de ao menos uma peça.");
      return;
    }
    setRrSaving(true);
    try {
      const { data } = await api.post(`/production-orders/${sel.id}/pickup-receipts`, {
        items, notes: rrForm.notes,
        receiver_name: rrForm.receiver_name, delivered_by: rrForm.delivered_by,
      });
      toast.success(`Recibo ${data.number} (Retirada ${data.seq}) emitido.`);
      setRrOpen(false);
      setRrForm({ items: {}, receiver_name: "", delivered_by: "", notes: "" });
      refreshOp(sel.id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRrSaving(false);
    }
  };

  const downloadReceiptPdf = async (r) => {
    setRrPdfLoading(r.id);
    try {
      const { data } = await api.get(`/pickup-receipts/${r.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${r.number}-retirada-${r.seq}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRrPdfLoading("");
    }
  };

  const cancelReceipt = async (r) => {
    const reason = window.prompt(`Cancelar o recibo ${r.number} (Retirada ${r.seq})?\nMotivo (opcional):`, "");
    if (reason === null) return;
    try {
      await api.post(`/pickup-receipts/${r.id}/cancel`, { reason });
      toast.success(`Recibo ${r.number} (Retirada ${r.seq}) cancelado.`);
      refreshOp(sel.id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadPdf = async (op) => {
    if (pdfLoading) return;
    setPdfLoading(op.id);
    try {
      const { data } = await api.get(`/production-orders/${op.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${op.number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPdfLoading("");
    }
  };

  const downloadFile = async (oid, itemIdx, fileIdx, name) => {
    try {
      const { data } = await api.get(`/production-orders/${oid}/files/${itemIdx}/${fileIdx}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = name || "arquivo"; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/production-orders/${sel.id}`, form);
      toast.success(`OP ${data.number} atualizada.`);
      setSel(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!items) {
    return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-600" /></div>;
  }

  return (
    <div data-testid="admin-production-page">
      <h1 className="display text-3xl font-extrabold uppercase">Ordens de Produção</h1>
      <p className="mt-1 text-sm text-slate-500">
        Geradas a partir de orçamentos aprovados. Fluxo: Planejamento → Em produção → Controle de qualidade → Concluído → Entrega.
      </p>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="op-board">
        {COLUMNS.map((status) => {
          const list = items.filter((o) => displayStatus(o.status) === status);
          return (
            <section key={status} className="card-tactical rounded-md p-4" data-testid={`op-col-${slug(status)}`}>
              <div className="flex items-center justify-between">
                <h2 className="display text-sm font-bold uppercase tracking-wide">{status}</h2>
                <span className="mono text-sm text-orange-400">{list.length}</span>
              </div>
              <ul className="mt-3 space-y-2.5">
                {list.map((o) => (
                  <li key={o.id} className="rounded-md border border-slate-800 bg-slate-900/40 hover:border-orange-900/60 transition-colors">
                    <button onClick={() => open(o)} data-testid={`op-card-${o.number}`}
                      className="w-full text-left p-3 pb-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="mono text-sm text-slate-200">{o.number}</span>
                        {o.priority !== "Normal" && (
                          <span className={`text-[9px] uppercase tracking-wider rounded-sm px-1.5 py-0.5 border ${o.priority === "Urgente" ? "text-red-400 border-red-900/60" : "text-amber-400 border-amber-900/60"}`}>
                            {o.priority}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-slate-400 truncate">{o.client_name}</div>
                      <div className="text-[11px] text-slate-600 truncate">
                        {o.items?.[0]?.part_name} · {o.items?.[0]?.quantity} un.
                      </div>
                      {(o.machine || o.deadline) && (
                        <div className="mt-1 text-[10px] mono text-slate-600 truncate">
                          {[o.machine, o.deadline].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </button>
                    <div className="flex justify-end px-3 pb-2">
                      <button
                        data-testid={`op-pdf-${o.number}`}
                        onClick={() => downloadPdf(o)}
                        disabled={pdfLoading === o.id}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-orange-400 transition-colors disabled:opacity-50"
                      >
                        {pdfLoading === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} PDF
                      </button>
                    </div>
                  </li>
                ))}
                {!list.length && (
                  <li className="py-5 text-center text-[11px] text-slate-700">
                    <Factory className="w-5 h-5 mx-auto mb-1.5 text-slate-800" strokeWidth={1.25} />
                    Vazio
                  </li>
                )}
              </ul>
            </section>
          );
        })}
      </div>

      {sel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setSel(null)}>
          <div className="card-tactical rounded-md p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} data-testid="op-detail-modal">
            <div className="flex items-center justify-between">
              <h2 className="display text-xl font-extrabold uppercase mono" data-testid="op-detail-number">{sel.number}</h2>
              <button onClick={() => setSel(null)} data-testid="op-detail-close"
                className="text-slate-500 hover:text-slate-300" aria-label="Fechar">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-1 text-xs text-slate-500">
              {sel.client_name} · Orçamento{" "}
              <Link to={`/solicitacoes/${sel.quote_id}`} data-testid="op-quote-link" className="text-orange-400 hover:text-orange-300 mono">
                {sel.quote_number}
              </Link>
              {" "}· {fdate(sel.created_at)}
            </div>
            <div className="mt-3 space-y-2" data-testid="op-items">
              {(sel.items || []).map((it, i) => (
                <div key={i} className="rounded-md border border-slate-800 bg-slate-900/40 p-3" data-testid={`op-item-${i}`}>
                  <div className="flex gap-3">
                    <OpThumb oid={sel.id} itemIdx={i} files={it.files} />
                    <div className="flex-1 min-w-0">
                      <div className="mono text-xs text-slate-200">{it.part_name}</div>
                      <div className="mt-0.5 text-[11px] text-slate-500">
                        {it.quantity} un.{it.material ? ` · ${it.material}` : ""}
                      </div>
                      {it.details && <div className="mt-0.5 text-[11px] text-slate-500">{it.details}</div>}
                      {(it.files || []).length > 0 && (
                        <ul className="mt-1.5 space-y-1">
                          {it.files.map((f, fi) => (
                            <li key={fi} className="flex items-center justify-between gap-2">
                              <span className="text-[11px] text-slate-400 truncate">{f.original_filename}</span>
                              <span data-testid={`op-file-origin-${i}-${fi}`}
                                className={`text-[9px] uppercase tracking-wider rounded-sm px-1 py-0.5 border shrink-0 ${f.origin === "admin" ? "border-orange-900/60 text-orange-400" : "border-slate-700 text-slate-500"}`}>
                                {f.origin === "admin" ? "Admin" : "Cliente"}
                              </span>
                              <button type="button" data-testid={`op-file-download-${i}-${fi}`}
                                onClick={() => downloadFile(sel.id, i, fi, f.original_filename)}
                                className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-orange-400 shrink-0 transition-colors">
                                <FileDown className="w-3 h-3" /> Baixar
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Status de produção</label>
                <select data-testid="op-status-select" value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                  {form.status && !COLUMNS.includes(form.status) && (
                    <option value={form.status}>{form.status} (legado)</option>
                  )}
                  {COLUMNS.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Prioridade</label>
                <select data-testid="op-priority-select" value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })} className={inputCls}>
                  {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Máquina</label>
                <select data-testid="op-machine-select" value={form.machine}
                  onChange={(e) => setForm({ ...form, machine: e.target.value })} className={inputCls}>
                  <option value="">— selecionar —</option>
                  {form.machine && !machines.some((m) => m.name === form.machine) && (
                    <option value={form.machine}>{form.machine}</option>
                  )}
                  {machines.map((m) => (
                    <option key={m.name} value={m.name}>{m.name}{m.type ? ` — ${m.type}` : ""}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Operador</label>
                <input data-testid="op-operator-input" value={form.operator}
                  onChange={(e) => setForm({ ...form, operator: e.target.value })} className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Prazo de produção (data prevista)</label>
                <input data-testid="op-deadline-input" type="date" value={form.deadline}
                  onChange={(e) => setForm({ ...form, deadline: e.target.value })} className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Observações internas</label>
                <textarea data-testid="op-notes-input" rows={3} value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} resize-y`} />
              </div>
            </div>

            <button data-testid="op-save-button" onClick={save} disabled={saving}
              className="mt-5 inline-flex items-center gap-2 pill-accent rounded-md px-6 py-2.5 text-sm font-semibold uppercase tracking-wider disabled:opacity-60 transition">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salvar OP
            </button>
            <button data-testid="op-pdf-button" onClick={() => downloadPdf(sel)} disabled={pdfLoading === sel.id}
              className="mt-5 ml-2 inline-flex items-center gap-2 border border-slate-700 hover:border-orange-500 text-slate-300 hover:text-orange-400 rounded-md px-6 py-2.5 text-sm font-semibold uppercase tracking-wider disabled:opacity-50 transition-colors">
              {pdfLoading === sel.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} PDF p/ fábrica
            </button>

            <div className="mt-5 border-t border-slate-800 pt-4" data-testid="op-receipts-section">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">
                  Recibos de retirada {rrNumber && <span className="mono text-orange-400">· {rrNumber}</span>}
                </div>
                <button data-testid="rr-generate-button" onClick={() => setRrOpen(!rrOpen)}
                  className="inline-flex items-center gap-1.5 pill-accent rounded-md px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider transition">
                  <ClipboardList className="w-3.5 h-3.5" /> Gerar Recibo de Retirada
                </button>
              </div>

              <ul className="mt-2 space-y-1" data-testid="rr-saldos">
                {(sel.items || []).map((it, i) => (
                  <li key={i} className="text-[11px] text-slate-500 mono" data-testid={`rr-saldo-${i}`}>
                    {it.part_name}: saldo pendente <span className="text-slate-300">{saldos[i] ?? it.quantity}</span> de {it.quantity} un.
                  </li>
                ))}
              </ul>

              {rrOpen && (
                <div className="mt-3 rounded-md border border-slate-800 bg-slate-900/40 p-3" data-testid="rr-form">
                  {(sel.items || []).map((it, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 py-1">
                      <span className="text-xs text-slate-300 truncate">{it.part_name}</span>
                      <input type="number" min="0" max={saldos[i] ?? it.quantity}
                        data-testid={`rr-item-qty-${i}`}
                        value={rrForm.items[i] ?? ""}
                        onChange={(e) => setRrForm({ ...rrForm, items: { ...rrForm.items, [i]: e.target.value } })}
                        placeholder={`0 / ${saldos[i] ?? it.quantity}`}
                        className="w-24 bg-slate-900/70 border border-slate-700 rounded-md px-2 py-1.5 text-xs outline-none focus:border-orange-500" />
                    </div>
                  ))}
                  <div className="mt-2 grid grid-cols-2 gap-3">
                    <input data-testid="rr-receiver-input" placeholder="Nome de quem retira"
                      value={rrForm.receiver_name}
                      onChange={(e) => setRrForm({ ...rrForm, receiver_name: e.target.value })} className={inputCls} />
                    <input data-testid="rr-delivered-input" placeholder="Nome de quem entrega"
                      value={rrForm.delivered_by}
                      onChange={(e) => setRrForm({ ...rrForm, delivered_by: e.target.value })} className={inputCls} />
                  </div>
                  <textarea data-testid="rr-notes-input" rows={2} placeholder="Observações (opcional)"
                    value={rrForm.notes}
                    onChange={(e) => setRrForm({ ...rrForm, notes: e.target.value })}
                    className={`${inputCls} resize-y mt-3`} />
                  <button data-testid="rr-submit-button" onClick={createReceipt} disabled={rrSaving}
                    className="mt-3 inline-flex items-center gap-2 pill-accent rounded-md px-4 py-2 text-xs font-semibold uppercase tracking-wider disabled:opacity-60 transition">
                    {rrSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardList className="w-3.5 h-3.5" />}
                    Emitir recibo
                  </button>
                </div>
              )}

              {receipts && receipts.length > 0 && (
                <ul className="mt-3 space-y-2" data-testid="rr-list">
                  {receipts.map((r) => (
                    <li key={r.id} data-testid={`rr-row-${r.id}`}
                      className={`rounded-md border p-2.5 bg-slate-900/40 ${r.status === "Cancelado" ? "border-red-900/50 opacity-60" : "border-slate-800"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="mono text-xs text-slate-200">{r.number} — Retirada {r.seq}</span>
                        <span className={`text-[9px] uppercase tracking-wider ${r.status === "Cancelado" ? "text-red-400" : "text-emerald-400"}`}>{r.status}</span>
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-500">
                        {fdate(r.created_at)} · {(r.items || []).map((it) => `${it.quantity}× ${it.part_name}`).join(", ")}
                        {r.receiver_name ? ` · retirado por ${r.receiver_name}` : ""}
                      </div>
                      {r.status === "Cancelado" && r.cancel_reason && (
                        <div className="mt-0.5 text-[10px] text-red-400/80">Motivo: {r.cancel_reason}</div>
                      )}
                      <div className="mt-1.5 flex gap-3">
                        <button data-testid={`rr-pdf-${r.id}`} onClick={() => downloadReceiptPdf(r)} disabled={rrPdfLoading === r.id}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-orange-400 transition-colors disabled:opacity-50">
                          {rrPdfLoading === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />} PDF
                        </button>
                        {r.status !== "Cancelado" && (
                          <button data-testid={`rr-cancel-${r.id}`} onClick={() => cancelReceipt(r)}
                            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-red-400 transition-colors">
                            <Ban className="w-3 h-3" /> Cancelar
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {finData && (
              <div className="mt-5 border-t border-slate-800 pt-4" data-testid="op-finance-section">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Financeiro da retirada</div>
                  <Link to="/financeiro" data-testid="op-finance-link"
                    className="text-[10px] uppercase tracking-wider text-orange-400 hover:text-orange-300 transition-colors">
                    Abrir Contas a Receber
                  </Link>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px] mono">
                  <span className="text-slate-500">Valor do orçamento</span>
                  <span className="text-right text-slate-200" data-testid="op-fin-quote-total">{brl(finData.quote_total)}</span>
                  <span className="text-slate-500">Última retirada</span>
                  <span className="text-right text-slate-200" data-testid="op-fin-last">{brl(finData.last_withdrawal_amount)}</span>
                  <span className="text-slate-500">Recebíveis anteriores</span>
                  <span className="text-right text-slate-200">{brl(finData.previous_receivables_total)}</span>
                  <span className="text-slate-500">Total gerado em recebíveis</span>
                  <span className="text-right text-orange-400" data-testid="op-fin-generated">{brl(finData.generated_total)}</span>
                  <span className="text-slate-500">Não convertido em recebível</span>
                  <span className="text-right text-slate-200">{brl(finData.pending_value)}</span>
                  <span className="text-slate-500">Forma / condição</span>
                  <span className="text-right text-slate-200">
                    {finData.payment_method_label}{finData.payment_terms && finData.payment_terms !== finData.payment_method_label ? ` · ${finData.payment_terms}` : ""}
                  </span>
                  <span className="text-slate-500">Situação financeira</span>
                  <span className="text-right" data-testid="op-fin-status">
                    <span className="text-sky-400">aberto {brl(finData.open_total)}</span>
                    {" · "}<span className="text-emerald-400">recebido {brl(finData.paid_total)}</span>
                    {finData.overdue_total > 0 && <>{" · "}<span className="text-red-400">vencido {brl(finData.overdue_total)}</span></>}
                  </span>
                </div>
                {finData.finance_pending && (
                  <div className="mt-2 text-[11px] text-amber-400" data-testid="op-fin-pending">
                    Há retirada com recebível pendente de geração — regularize o orçamento e reabra a retirada.
                  </div>
                )}
              </div>
            )}

            {sel.history?.length > 0 && (
              <div className="mt-5 border-t border-slate-800 pt-4" data-testid="op-history">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Histórico</div>
                <ul className="mt-2 space-y-1.5">
                  {sel.history.map((h, i) => (
                    <li key={i} className="text-[11px] text-slate-400 mono" data-testid={`op-history-${i}`}>
                      {fdate(h.at)} — {h.field ? `${h.field}: ` : ""}{h.from || "—"} → <span className="text-orange-400">{h.to}</span>
                      {h.by ? <span className="text-slate-600"> · {h.by}</span> : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
